import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import CreditCardIcon from '@/assets/all-icons/credit-card-icon';
import config from '@/config';
import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useOverlayHistory from '@/hooks/useOverlayHistory';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import useTranslations from '@/i18n/useTranslations';
import ChannelsService from '@/services/channels.service';
import fetchWrapper from '@/services/fetchWrapper.service';
import paymentProfileService from '@/services/paymentProfile.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import BillingDetailsCard from '@/shared-components/payment-profile/BillingDetailsCard';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolvePaymentCountryHint } from '@/utils/paymentCountry.utils';

import './styles/SubscriptionPreview.css';

function isOwnerOnlyWritePermission(writePerms) {
  return String(writePerms || '').trim().toLowerCase() === 'none';
}

export default function SubscriptionPreview({ channelId: providedChannelId, ownerUsername: providedOwnerUsername, onClose }) {
  const navigate = useNavigate();
  const { ownerUsername: routeOwnerUsername, channelId: routeChannelId } = useParams();
  const ownerUsername = providedOwnerUsername || routeOwnerUsername;
  const channelId = providedChannelId || routeChannelId;
  const location = useLocation();
  const t = useTranslations('common.profile.channels');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [channel, setChannel] = useState(null);
  const [awaitingAccess, setAwaitingAccess] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const paymentProfile = usePaymentProfile();
  const profileBillingAddress = paymentProfile?.profile?.billing_address ?? null;
  const profileBillingInfo = paymentProfile?.profile?.billing_info ?? null;
  const [billingAddress, setBillingAddress] = useState(() => profileBillingAddress);
  const [billingInfo, setBillingInfo] = useState(() => profileBillingInfo);
  const buyerCountryHint = resolvePaymentCountryHint({ profile: paymentProfile?.profile, billingAddress });
  const canonicalOwnerUsername = useMemo(() => (
    channel?.owner?.user_username
    || channel?.owner_username
    || ownerData?.user_username
    || ownerUsername
  ), [channel?.owner?.user_username, channel?.owner_username, ownerData?.user_username, ownerUsername]);
  const ownerDisplayName = ownerData?.user_name || channel?.owner?.user_name || canonicalOwnerUsername;
  const ownerProfileImage = ownerData?.user_image || channel?.owner?.user_image || null;
  const ownerProfileType = ownerData?.user_type || ownerData?.__t || channel?.owner?.user_type || channel?.owner?.__t || 'user';

  const closePreview = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
      return;
    }
    navigate('/channels/discover', { replace: true });
  }, [navigate, onClose]);
  useOverlayHistory({ open: true, onClose: closePreview });

  // Load owner and channel data; if already a member, go directly to channel
  useEffect(() => {
    let mounted = true;
    async function run() {
      try {
        setLoading(true);

        let ownerId = null;

        // Try the owner slug first, but do not depend on it being correct.
        const { response: r1, data: d1 } = await fetchWrapper.get(`${config.apis.api.url}/user/public/${ownerUsername}`);
        if (r1?.ok && d1) {
          ownerId = d1?._id || d1?.user_id || null;
          if (mounted) {
            setOwnerData(d1);
          }
        }

        // If already a member of this channel, go straight to channel
        const { response: rM, data: dM } = await ChannelsService.getMemberOf('me');
        if (!mounted) return;
        if (rM?.ok) {
          const list = Array.isArray(dM?.data) ? dM.data : [];
          const alreadyMember = list.some((ch) => String(ch?._id) === String(channelId));
          if (alreadyMember) {
            navigate(`/channel/${canonicalOwnerUsername}/${channelId}`, { replace: true });
            return;
          }
        }

        let found = null;

        if (ownerId) {
          const { response: r2, data: d2 } = await ChannelsService.getByOwner(ownerId);
          if (r2?.ok) {
            const list = Array.isArray(d2?.data) ? d2.data : [];
            found = list.find((ch) => String(ch._id) === String(channelId)) || null;
          }
        }

        // Fallback: channelId is authoritative even when the owner slug is stale or mismatched.
        if (!found) {
          const { response: rFallback, data: dFallback } = await ChannelsService.getOne(channelId);
          if (!rFallback?.ok) throw new Error('Channel not found');

          found = dFallback?.data && !Array.isArray(dFallback.data)
            ? dFallback.data
            : dFallback?.data || dFallback;

          if (!found) throw new Error('Channel not found');

          const fallbackOwner = found?.owner && typeof found.owner === 'object' ? found.owner : null;
          if (fallbackOwner && mounted) {
            setOwnerData((prev) => prev || fallbackOwner);
          }
        }

        if (!mounted) return;
        setChannel(found);
        setError(null);
      } catch (e) {
        setError(e.message || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    run();
    return () => { mounted = false; };
  }, [canonicalOwnerUsername, ownerUsername, channelId, navigate]);

  const isPaid = useMemo(() => {
    if (!channel) return false;
    return channel.type === 'subscribers' && Number(channel.price) > 0;
  }, [channel]);
  useEffect(() => {
    setBillingAddress(profileBillingAddress ?? null);
    setBillingInfo(profileBillingInfo ?? null);
  }, [profileBillingAddress, profileBillingInfo]);

  useEffect(() => {
    if (!isPaid) return;
    let active = true;
    paymentProfileService.listPaymentMethods().then(({ response, data }) => {
      if (!active || !response?.ok) return;
      const methods = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
      setPaymentMethods(methods);
      setSelectedPaymentMethodId((current) => current
        || data?.default_payment_method_id
        || methods[0]?.id
        || null);
    }).catch(() => {});
    return () => { active = false; };
  }, [isPaid]);

  useEffect(() => {
    if (!isPaid || !channel?._id) return;
    let active = true;
    ChannelsService.getSubscriptionQuote(channel._id, { paymentMethodId: selectedPaymentMethodId, buyerCountry: buyerCountryHint })
      .then(({ response, data }) => {
        if (active && response?.ok) setQuote(data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [buyerCountryHint, channel?._id, isPaid, selectedPaymentMethodId]);

  const cameFromExpired = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('expired') === '1';
  }, [location.search]);

  // After returning from Stripe with ?paid=1, ensure access if webhooks are delayed
  useEffect(() => {
    if (!channel || !isPaid) {
      setAwaitingAccess(false);
      return;
    }

    const searchParams = new URLSearchParams(location.search || '');
    if (searchParams.get('paid') !== '1') {
      setAwaitingAccess(false);
      return;
    }

    const targetChannelId = channel?._id ?? channelId;
    if (!targetChannelId) return;

    setAwaitingAccess(true);

    let attempts = 0;
    let cancelled = false;
    let timeoutId = null;

    const checkMembership = async () => {
      attempts += 1;

      try {
        const { response, data } = await ChannelsService.getMemberOf('me');
        if (!cancelled && response?.ok) {
          const list = Array.isArray(data?.data) ? data.data : [];
          const isMember = list.some((entry) => String(entry?._id ?? entry?.id) === String(targetChannelId));
          if (isMember) {
            cancelled = true;
            setAwaitingAccess(false);
            navigate(`/channel/${canonicalOwnerUsername}/${channelId}`, { replace: true });
            return;
          }
        }
      } catch {
        if (cancelled) return;
      }

      // Keep nudging the server-side projection while waiting for webhook propagation.
      if (attempts === 1 || attempts % 5 === 0) {
        try {
          await ChannelsService.ensureSubscription(targetChannelId);
        } catch {
          // keep polling
        }
      }

      if (!cancelled) {
        timeoutId = setTimeout(checkMembership, 1000);
      }
    };

    checkMembership();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [canonicalOwnerUsername, channel, channelId, isPaid, location.search, navigate]);

  const buttonLabel = useMemo(() => {
    if (!channel) return '...';
    if (!isPaid) return 'Join VIP';

    const formattedMonthlyPrice = formatCurrencyAmount(channel.price, channel.currency || 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return formattedMonthlyPrice
      ? `Subscribe for ${formattedMonthlyPrice}/month`
      : 'Subscribe';
  }, [channel, isPaid]);

  const bannerSrc = useMemo(() => {
    const image = channel?.image;
    if (!image) return null;
    if (typeof image === 'string' && (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('blob:'))) {
      return image;
    }
    return `${config.app.imageServiceDomain}/${image}/w=1600`;
  }, [channel?.image]);

  async function handleAddPaymentMethod() {
    if (typeof paymentProfile?.ensurePaymentProfile !== 'function') return;

    setAddingPaymentMethod(true);
    setError(null);
    try {
      await paymentProfile.ensurePaymentProfile({ forceSetup: true });
      const { response, data } = await paymentProfileService.listPaymentMethods();
      if (!response?.ok) throw new Error('Unable to load payment methods.');

      const methods = Array.isArray(data?.payment_methods) ? data.payment_methods : [];
      const paymentMethodId = data?.default_payment_method_id || methods[0]?.id || null;
      setPaymentMethods(methods);
      setSelectedPaymentMethodId(paymentMethodId);
    } catch (error) {
      if (error?.code !== 'BIDDER_PROFILE_CANCELLED') {
        setError(error?.message || 'Unable to add a payment method.');
      }
    } finally {
      setAddingPaymentMethod(false);
    }
  }

  async function handleCTA() {
    try {
      if (!channel) return;
      if (!isPaid) {
        await ChannelsService.join(channel._id);
      } else {
        setPaymentLoading(true);
        setError(null);
        let paymentMethodId = selectedPaymentMethodId;
        if (!paymentMethodId) {
          await paymentProfile?.ensurePaymentProfile?.({ forceSetup: true, countryHint: buyerCountryHint ?? undefined });
          const { response, data } = await paymentProfileService.listPaymentMethods();
          const methods = response?.ok && Array.isArray(data?.payment_methods) ? data.payment_methods : [];
          setPaymentMethods(methods);
          paymentMethodId = data?.default_payment_method_id || methods[0]?.id || null;
          setSelectedPaymentMethodId(paymentMethodId);
        }
        if (!paymentMethodId) throw new Error('Add a payment method before subscribing.');

        const { response, data } = await ChannelsService.createSubscription(channel._id, { paymentMethodId, buyerCountry: buyerCountryHint });
        if (!response?.ok) throw new Error(data?.message || 'Unable to start subscription payment.');
        if (data?.client_secret && (data?.requires_action || ['requires_action', 'requires_confirmation'].includes(data?.status))) {
          const key = stripeConfig.publishableKey || await resolveStripePublishableKey();
          const stripe = key ? await loadStripe(key) : null;
          if (!stripe) throw new Error('Stripe is unavailable.');
          const result = await stripe.confirmCardPayment(data.client_secret, { payment_method: paymentMethodId });
          if (result?.error) throw result.error;
        }
        const finalized = await ChannelsService.finalizeSubscription(channel._id, data?.stripe_subscription_id);
        if (!finalized?.response?.ok) throw new Error(finalized?.data?.message || 'Payment is still processing.');
        navigate(`/channel/${canonicalOwnerUsername}/${channelId}`, { replace: true });
        return;
      }
    } catch (error) {
      setError(error?.message || 'Unable to complete subscription payment.');
    } finally {
      setPaymentLoading(false);
      if (!isPaid) {
        navigate(`/channel/${canonicalOwnerUsername}/${channelId}`);
      }
    }
  }

  function handleClose() {
    closePreview();
  }

  function handleOwnerProfileClick() {
    onClose?.();
  }

  const content = (
    <>
      <div className="subscription-preview-popup__header element-non-selectable">
        <h3 className="subscription-preview-popup__title">Join VIP</h3>
        {!awaitingAccess ? (
          <XIcon
            className="subscription-preview-popup__close"
            onClick={handleClose}
          />
        ) : null}
      </div>
      {loading ? (
        <div className="subscription-preview__loading" role="status" aria-label="Loading subscription preview">
          <Loader inline thin />
        </div>
      ) : awaitingAccess ? (
        <div className="subscription-preview__pending-access">
          <Loader inline thin />
          <p className="subscription-preview__pending-text">
            Processing payment and granting access. Please wait…
          </p>
        </div>
      ) : (
        <div className="subscription-preview__content">
          <div className="subscription-preview__banner-wrapper">
            <div className="subscription-preview__banner-container">
              {bannerSrc ? (
                <img
                  className="subscription-preview__banner-image"
                  src={bannerSrc}
                  alt="Channel cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              ) : (
                <div className="subscription-preview__banner-placeholder" />
              )}
            </div>
          </div>

          {cameFromExpired && (
            <div className="subscription-preview__notice">
              Your previous access ended. Renewed subscriptions start immediately.
            </div>
          )}

          {canonicalOwnerUsername && (
            <Link
              className="subscription-preview__owner"
              to={`/user/${canonicalOwnerUsername}`}
              onClick={handleOwnerProfileClick}
            >
              <UserProfileImage
                image={ownerProfileImage}
                typeOfProfile={ownerProfileType}
                className="subscription-preview__owner-avatar"
              />
              <div className="subscription-preview__owner-details">
                <div className="subscription-preview__owner-name">{ownerDisplayName}</div>
                <div className="subscription-preview__owner-username">@{canonicalOwnerUsername}</div>
              </div>
            </Link>
          )}

          <div className="subscription-preview__description">
            {channel?.description || ''}
          </div>

          <div className="subscription-preview__includes-label">{t('subscription_preview.unique_benefits_label')}</div>
          <div className="subscription-preview__benefits-container">
            <ul className="subscription-preview__benefits-list">
              {isOwnerOnlyWritePermission(channel?.write_perms)
                ? <li>Only {ownerUsername} can write in the channel</li>
                : <li>Interact in {ownerUsername}&apos;s VIP</li>}
              <li>Early 72h access to {ownerUsername}&apos;s artworks</li>
              <li>AI access for {ownerUsername}&apos;s profile and artworks</li>
            </ul>
          </div>

          {isPaid && (
            <>
              <div className="subscription-preview__totals">
                <div>
                  <span>Subscription</span>
                  <strong>{formatCurrencyAmount(quote?.price ?? channel.price, quote?.currency ?? channel.currency ?? 'EUR')}</strong>
                </div>
                <div>
                  <span>VAT</span>
                  <strong>{formatCurrencyAmount(quote?.vatAmount ?? 0, quote?.currency ?? channel.currency ?? 'EUR')}</strong>
                </div>
                <div>
                  <span>Total / month</span>
                  <strong>{formatCurrencyAmount(quote?.total ?? channel.price, quote?.currency ?? channel.currency ?? 'EUR')}</strong>
                </div>
              </div>
              <div className="subscription-preview__payment-methods">
                <div className="subscription-preview__payment-methods-copy">
                  <CreditCardIcon className="subscription-preview__payment-methods-icon" />
                  <div>
                    <p className="subscription-preview__payment-methods-title">Payment method</p>
                    {!paymentMethods.length && (
                      <p className="subscription-preview__payment-methods-message">No payment methods saved yet.</p>
                    )}
                  </div>
                </div>
                {paymentMethods.length > 0 && (
                  <div className="subscription-preview__payment-methods-list">
                    {paymentMethods.map((method) => (
                      <button type="button" key={method.id} className={method.id === selectedPaymentMethodId ? 'active' : ''} onClick={() => setSelectedPaymentMethodId(method.id)}>
                        {(method.card?.brand || method.brand || 'Card').toUpperCase()} •••• {method.card?.last4 || method.last4 || ''}
                      </button>
                    ))}
                  </div>
                )}
                {!paymentMethods.length && (
                  <ContinueButton
                    className="support-public__payment-add"
                    onClick={handleAddPaymentMethod}
                    disabled={addingPaymentMethod}
                  >
                    {addingPaymentMethod ? (
                      <Loader inline className="support-public__payment-add-spinner" thin />
                    ) : (
                      'Add payment method'
                    )}
                  </ContinueButton>
                )}
              </div>
              <BillingDetailsCard
                className="subscription-preview__billing"
                billingAddress={billingAddress}
                billingInfo={billingInfo}
                onSaved={({ billingAddress: nextAddress, billingInfo: nextInfo }) => {
                  setBillingAddress(nextAddress ?? null);
                  setBillingInfo(nextInfo ?? null);
                }}
                triggerOnly
              />
            </>
          )}
        </div>
      )}
    </>
  );

  // Render as a GenericPopup overlaying the channels layout
  return (
    <GenericPopup
      open={true}
      onConfirm={loading || awaitingAccess || paymentLoading ? undefined : handleCTA}
      confirmButtonText={awaitingAccess || paymentLoading ? 'Processing payment...' : (loading ? 'Loading...' : buttonLabel)}
      opacity
      className="generic-popup_content subscription-preview-popup"
    >
      {error ? (
        <div style={{ padding: 16, color: 'var(--app-grey-color)' }}>{error}</div>
      ) : (
        content
      )}
    </GenericPopup>
  );
}
